import { useState, useMemo, useEffect } from 'react';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import Modal from '../components/Modal';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  XMarkIcon,
  KeyIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  UserGroupIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import {
  solicitantesService,
  type CreateSolicitanteDto,
  type UpdateSolicitanteDto,
  type Solicitante,
  type ListSolicitantesParams,
  type BatchSolicitanteDto,
} from '../services/solicitantes.service';
import { formatPhone } from '../utils/formatters';
import { unmask } from '../utils/masks';
import FormattedInput from '../components/FormattedInput';
import FormField from '../components/FormField';
import PageContainer from '../components/PageContainer';
import PageHeader from '../components/PageHeader';
import { useEmpresa } from '../contexts/EmpresaContext';

export default function Solicitantes() {
  const { empresa, isAdmin } = useEmpresa();
  const queryClient = useQueryClient();

  // Paginação
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // Filtros
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterHasUser, setFilterHasUser] = useState<'all' | 'with_user' | 'without_user'>('all');
  const [filterGroupId, setFilterGroupId] = useState<number | 'all'>('all');

  // Debounce na busca (300ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Modais de Criação / Edição
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSolicitante, setEditingSolicitante] = useState<Solicitante | null>(null);

  // Modal de Redefinir Senha
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [resetTargetSolicitante, setResetTargetSolicitante] = useState<Solicitante | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetSendEmail, setResetSendEmail] = useState(true);
  const [resetMustChange, setResetMustChange] = useState(true);
  const [showResetPassword, setShowResetPassword] = useState(false);

  // Senha do Modal Principal
  const [showInitialPassword, setShowInitialPassword] = useState(false);

  // Seleção em Massa
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isBatchProfileModalOpen, setIsBatchProfileModalOpen] = useState(false);
  const [batchTargetProfileId, setBatchTargetProfileId] = useState<number>(8);

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%&*';
    const randomBuffer = new Uint32Array(10);
    window.crypto.getRandomValues(randomBuffer);
    let result = '';
    for (let i = 0; i < 10; i++) {
      result += chars.charAt(randomBuffer[i] % chars.length);
    }
    return result;
  };

  const { data: perfisData } = useQuery({
    queryKey: ['solicitantes-perfis'],
    queryFn: () => solicitantesService.getPerfis(),
  });

  const availableProfiles = useMemo(() => {
    if (perfisData && perfisData.length > 0) {
      return perfisData.map((g) => {
        let role = 'ADMIN';
        let bg = 'bg-indigo-50 border-indigo-200 text-indigo-700';
        const lowerName = g.name.toLowerCase();
        if (
          lowerName === 'cliente-agendadores' ||
          lowerName.includes('agendador') ||
          lowerName.includes('operador')
        ) {
          role = 'AGENDADOR';
          bg = 'bg-sky-50 border-sky-200 text-sky-700';
        } else if (lowerName === 'cliente-tecnicos' || lowerName.includes('tecnic')) {
          role = 'TECNICO';
          bg = 'bg-purple-50 border-purple-200 text-purple-700';
        } else if (lowerName === 'cliente-financeiro' || lowerName.includes('financ')) {
          role = 'FINANCEIRO';
          bg = 'bg-amber-50 border-amber-200 text-amber-700';
        } else if (lowerName === 'cliente-integracoes' || lowerName.includes('integra')) {
          role = 'INTEGRACOES';
          bg = 'bg-emerald-50 border-emerald-200 text-emerald-700';
        }
        return {
          id: g.id,
          role,
          name: g.name,
          label: g.description?.split(' - ')[0] || g.name,
          desc: g.description?.split(' - ')[1] || g.description || '',
          bg,
        };
      });
    }
    return [
      {
        id: 8,
        role: 'ADMIN',
        name: 'cliente-administradores',
        label: 'Administrador Geral',
        desc: 'Acesso total + gestão de usuários',
        bg: 'bg-indigo-50 border-indigo-200 text-indigo-700',
      },
      {
        id: 9,
        role: 'AGENDADOR',
        name: 'cliente-agendadores',
        label: 'Agendador de Exames',
        desc: 'Agendamentos e periódicos',
        bg: 'bg-sky-50 border-sky-200 text-sky-700',
      },
      {
        id: 10,
        role: 'TECNICO',
        name: 'cliente-tecnicos',
        label: 'Técnico SST',
        desc: 'Compliance e EPIs',
        bg: 'bg-purple-50 border-purple-200 text-purple-700',
      },
      {
        id: 11,
        role: 'FINANCEIRO',
        name: 'cliente-financeiro',
        label: 'Financeiro',
        desc: 'Faturas e boletos',
        bg: 'bg-amber-50 border-amber-200 text-amber-700',
      },
      {
        id: 12,
        role: 'INTEGRACOES',
        name: 'cliente-integracoes',
        label: 'Integrações',
        desc: 'Gestão de integrações e API REST',
        bg: 'bg-emerald-50 border-emerald-200 text-emerald-700',
      },
    ];
  }, [perfisData]);

  const [formData, setFormData] = useState<CreateSolicitanteDto>({
    nome: '',
    email: '',
    telefone: '',
    create_portal_user: true,
    initial_password: '',
    send_access_email: true,
    must_change_password: true,
    role_profile: 'SOLICITANTE_AGENDAMENTOS',
    role: 'ADMIN',
    group_id: undefined,
    scope_type: 'empresa_atual',
    vincular_grupo_economico: false,
  });

  const activeGroupId = useMemo(() => {
    if (formData.group_id && availableProfiles.some((p) => p.id === formData.group_id)) {
      return formData.group_id;
    }
    return availableProfiles[0]?.id || 8;
  }, [availableProfiles, formData.group_id]);

  // Dialog States
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info',
  });

  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant?: 'danger' | 'warning' | 'info' | 'success' | 'primary';
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'warning',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
    onConfirm: () => {},
  });

  const showAlert = (
    title: string,
    message: string,
    variant: 'success' | 'error' | 'warning' | 'info' = 'info',
  ) => {
    setAlertState({ isOpen: true, title, message, variant });
  };

  const hasActiveFilters = useMemo(() => {
    return (
      debouncedSearch !== '' ||
      filterStatus !== 'all' ||
      filterHasUser !== 'all' ||
      filterGroupId !== 'all'
    );
  }, [debouncedSearch, filterStatus, filterHasUser, filterGroupId]);

  const clearAllFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setFilterStatus('all');
    setFilterHasUser('all');
    setFilterGroupId('all');
    setPage(1);
  };

  const apiFilters = useMemo(() => {
    const params: ListSolicitantesParams = {
      page,
      limit,
    };
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    if (filterStatus !== 'all') {
      params.status = filterStatus;
    }
    if (filterHasUser !== 'all') {
      params.has_user = filterHasUser;
    }
    if (filterGroupId !== 'all') {
      params.group_id = filterGroupId;
    }
    return params;
  }, [page, limit, debouncedSearch, filterStatus, filterHasUser, filterGroupId]);

  const { data: response, isLoading } = useQuery({
    queryKey: ['solicitantes', empresa?.id, apiFilters],
    queryFn: () => solicitantesService.findAll(apiFilters),
    enabled: !!empresa?.id,
  });

  const solicitantes = response?.data || [];
  const pagination = response?.meta || {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  };

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateSolicitanteDto) => solicitantesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['solicitantes'] });
      setIsModalOpen(false);
      resetForm();
      showAlert('Sucesso', 'Solicitante cadastrado com sucesso!', 'success');
    },
    onError: (error: unknown) => {
      console.error('❌ Erro ao criar solicitante:', error);
      const axiosErr = error as { response?: { data?: { message?: string } } };
      showAlert(
        'Erro',
        axiosErr?.response?.data?.message || 'Erro ao criar solicitante',
        'error',
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateSolicitanteDto }) =>
      solicitantesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['solicitantes'] });
      setIsModalOpen(false);
      setEditingSolicitante(null);
      resetForm();
      showAlert('Sucesso', 'Dados do solicitante atualizados com sucesso!', 'success');
    },
    onError: (error: unknown) => {
      console.error('❌ Erro ao atualizar solicitante:', error);
      const axiosErr = error as { response?: { data?: { message?: string } } };
      showAlert(
        'Erro',
        axiosErr?.response?.data?.message || 'Erro ao atualizar solicitante',
        'error',
      );
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (id: number) => solicitantesService.toggleStatus(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['solicitantes'] });
      showAlert('Sucesso', data.message || 'Status alterado com sucesso!', 'success');
    },
    onError: (error: unknown) => {
      console.error('❌ Erro ao alterar status:', error);
      const axiosErr = error as { response?: { data?: { message?: string } } };
      showAlert(
        'Erro',
        axiosErr?.response?.data?.message || 'Erro ao alterar status do solicitante',
        'error',
      );
    },
  });

  const togglePortalAccessMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      solicitantesService.togglePortalAccess(id, enabled),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['solicitantes'] });
      showAlert('Sucesso', data.message || 'Acesso ao portal atualizado com sucesso!', 'success');
    },
    onError: (error: unknown) => {
      console.error('❌ Erro ao alterar acesso ao portal:', error);
      const axiosErr = error as { response?: { data?: { message?: string } } };
      showAlert(
        'Erro',
        axiosErr?.response?.data?.message || 'Erro ao alterar acesso ao portal',
        'error',
      );
    },
  });

  const reenviarConviteMutation = useMutation({
    mutationFn: (id: number) => solicitantesService.reenviarConvite(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['solicitantes'] });
      showAlert('Sucesso', data.message || 'Convite reenviado com sucesso!', 'success');
    },
    onError: (error: unknown) => {
      console.error('❌ Erro ao reenviar convite:', error);
      const axiosErr = error as { response?: { data?: { message?: string } } };
      showAlert(
        'Erro',
        axiosErr?.response?.data?.message || 'Erro ao reenviar convite',
        'error',
      );
    },
  });

  const redefinirSenhaMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: { password?: string; send_email?: boolean; must_change_password?: boolean };
    }) => solicitantesService.redefinirSenha(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['solicitantes'] });
      setIsResetPasswordModalOpen(false);
      setResetTargetSolicitante(null);
      if (data.password) {
        navigator.clipboard.writeText(data.password).catch(() => {});
        showAlert(
          'Senha Redefinida!',
          `A nova senha provisória é "${data.password}". Ela foi copiada para sua área de transferência.`,
          'success',
        );
      } else {
        showAlert('Sucesso', 'Senha redefinida com sucesso!', 'success');
      }
    },
    onError: (error: unknown) => {
      console.error('❌ Erro ao redefinir senha:', error);
      const axiosErr = error as { response?: { data?: { message?: string } } };
      showAlert(
        'Erro',
        axiosErr?.response?.data?.message || 'Erro ao redefinir senha',
        'error',
      );
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => solicitantesService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['solicitantes'] });
      showAlert('Sucesso', 'Solicitante inativado com sucesso!', 'success');
    },
    onError: (error: unknown) => {
      console.error('❌ Erro ao inativar solicitante:', error);
      const axiosErr = error as { response?: { data?: { message?: string } } };
      showAlert(
        'Erro',
        axiosErr?.response?.data?.message || 'Erro ao inativar solicitante',
        'error',
      );
    },
  });

  const batchActionMutation = useMutation({
    mutationFn: (data: BatchSolicitanteDto) => solicitantesService.batchAction(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['solicitantes'] });
      setSelectedIds([]);
      setIsBatchProfileModalOpen(false);
      showAlert('Sucesso', data.message || 'Ação em massa executada com sucesso!', 'success');
    },
    onError: (error: unknown) => {
      console.error('❌ Erro na ação em massa:', error);
      const axiosErr = error as { response?: { data?: { message?: string } } };
      showAlert(
        'Erro na Ação em Massa',
        axiosErr?.response?.data?.message || 'Não foi possível concluir a ação em massa.',
        'error',
      );
    },
  });

  const grupoEconomicoMeta = response?.meta?.grupo_economico || null;

  const resetForm = () => {
    const defaultProfile = availableProfiles[0];
    const hasGrupo = Boolean(grupoEconomicoMeta?.id);
    setFormData({
      nome: '',
      email: '',
      telefone: '',
      create_portal_user: true,
      initial_password: generateRandomPassword(),
      send_access_email: true,
      must_change_password: true,
      role_profile: 'SOLICITANTE_AGENDAMENTOS',
      role: defaultProfile?.role || 'ADMIN',
      group_id: defaultProfile?.id || 8,
      scope_type: hasGrupo ? 'grupo_economico' : 'empresa_atual',
      vincular_grupo_economico: hasGrupo,
    });
    setEditingSolicitante(null);
    setShowInitialPassword(false);
  };

  const handleOpenCreate = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleOpenEdit = (solicitante: Solicitante) => {
    const matchedProfile = solicitante.group_id
      ? availableProfiles.find((p) => p.id === solicitante.group_id)
      : availableProfiles.find(
          (p) => p.name === solicitante.group_name || p.role === solicitante.role,
        );
    const isGrupo = Boolean(solicitante.is_grupo_economico || solicitante.grupo_economico_id);

    setEditingSolicitante(solicitante);
    setFormData({
      nome: solicitante.nome || '',
      email: solicitante.email,
      telefone: solicitante.telefone || '',
      create_portal_user: false,
      role: solicitante.role || matchedProfile?.role || 'ADMIN',
      group_id: solicitante.group_id
        ? Number(solicitante.group_id)
        : matchedProfile?.id || availableProfiles[0]?.id || 8,
      scope_type: isGrupo ? 'grupo_economico' : 'empresa_atual',
      vincular_grupo_economico: isGrupo,
    });
    setIsModalOpen(true);
  };

  // Toggle Switch Individual com Confirmação
  const handleToggleStatus = (solicitante: Solicitante) => {
    const isAtivo = solicitante.enabled !== false;
    const nomeExibicao = solicitante.nome || solicitante.email;

    if (isAtivo) {
      setConfirmState({
        isOpen: true,
        title: 'Inativar Solicitante',
        message: `Tem certeza que deseja inativar o acesso de "${nomeExibicao}"? O usuário perderá o acesso imediato ao portal do cliente e não poderá realizar agendamentos ou solicitações.`,
        variant: 'danger',
        confirmText: 'Sim, Inativar Usuário',
        cancelText: 'Cancelar',
        onConfirm: () => toggleStatusMutation.mutate(solicitante.id),
      });
    } else {
      const perfilNome =
        solicitante.group?.name || solicitante.role || 'Administrador';
      setConfirmState({
        isOpen: true,
        title: 'Ativar Solicitante',
        message: `Deseja restaurar o acesso de "${nomeExibicao}" ao portal do cliente? O usuário voltará a ter acesso ativo com o perfil "${perfilNome}".`,
        variant: 'success',
        confirmText: 'Sim, Ativar Usuário',
        cancelText: 'Cancelar',
        onConfirm: () => toggleStatusMutation.mutate(solicitante.id),
      });
    }
  };

  const handleReenviarConvite = (solicitante: Solicitante) => {
    setConfirmState({
      isOpen: true,
      title: 'Reenviar Convite de Acesso',
      message: `Uma nova senha temporária será gerada e enviada para o e-mail "${solicitante.email}". Deseja continuar?`,
      variant: 'primary',
      confirmText: 'Reenviar Convite',
      cancelText: 'Cancelar',
      onConfirm: () => reenviarConviteMutation.mutate(solicitante.id),
    });
  };

  const handleRemove = (solicitante: Solicitante) => {
    setConfirmState({
      isOpen: true,
      title: 'Remover Solicitante',
      message: `Deseja desativar o cadastro de "${solicitante.nome || solicitante.email}"?`,
      variant: 'danger',
      confirmText: 'Desativar',
      cancelText: 'Cancelar',
      onConfirm: () => removeMutation.mutate(solicitante.id),
    });
  };

  const handleOpenResetPassword = (solicitante: Solicitante) => {
    setResetTargetSolicitante(solicitante);
    setResetPassword(generateRandomPassword());
    setResetSendEmail(true);
    setResetMustChange(true);
    setShowResetPassword(false);
    setIsResetPasswordModalOpen(true);
  };

  // Ações em Massa
  const handleSelectAll = () => {
    if (selectedIds.length === solicitantes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(solicitantes.map((s) => s.id));
    }
  };

  const handleToggleSelectRow = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleBatchDeactivate = () => {
    setConfirmState({
      isOpen: true,
      title: 'Inativar Solicitantes em Massa',
      message: `Tem certeza que deseja inativar os ${selectedIds.length} solicitantes selecionados? Todos perderão imediatamente o acesso ao portal do cliente e não poderão realizar solicitações ou agendamentos.`,
      variant: 'danger',
      confirmText: `Inativar ${selectedIds.length} Solicitante(s)`,
      cancelText: 'Cancelar',
      onConfirm: () =>
        batchActionMutation.mutate({
          ids: selectedIds,
          action: 'deactivate',
        }),
    });
  };

  const handleBatchActivate = () => {
    setConfirmState({
      isOpen: true,
      title: 'Ativar Solicitantes em Massa',
      message: `Deseja ativar o acesso dos ${selectedIds.length} solicitantes selecionados ao portal do cliente?`,
      variant: 'success',
      confirmText: `Ativar ${selectedIds.length} Solicitante(s)`,
      cancelText: 'Cancelar',
      onConfirm: () =>
        batchActionMutation.mutate({
          ids: selectedIds,
          action: 'activate',
        }),
    });
  };

  const handleBatchResendInvites = () => {
    setConfirmState({
      isOpen: true,
      title: 'Reenviar Convites em Massa',
      message: `Deseja reenviar o convite de acesso para os ${selectedIds.length} solicitantes selecionados? Uma nova senha temporária será gerada e enviada para o e-mail de cada um.`,
      variant: 'primary',
      confirmText: `Reenviar para ${selectedIds.length} Usuário(s)`,
      cancelText: 'Cancelar',
      onConfirm: () =>
        batchActionMutation.mutate({
          ids: selectedIds,
          action: 'resend_invites',
        }),
    });
  };

  const handleOpenBatchProfileModal = () => {
    setBatchTargetProfileId(availableProfiles[0]?.id || 8);
    setIsBatchProfileModalOpen(true);
  };

  const handleExecuteBatchProfile = () => {
    const chosenProfile =
      availableProfiles.find((p) => p.id === batchTargetProfileId) ||
      availableProfiles[0];

    batchActionMutation.mutate({
      ids: selectedIds,
      action: 'change_profile',
      group_id: chosenProfile.id,
      role: chosenProfile.role,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nome.trim() || !formData.email.trim()) {
      showAlert(
        'Atenção',
        'Preencha todos os campos obrigatórios marcados com asterisco (*).',
        'warning',
      );
      return;
    }

    const resolvedGroup =
      availableProfiles.find(
        (p) => p.id === (formData.group_id || activeGroupId),
      ) || availableProfiles[0];

    const submitData = {
      ...formData,
      group_id: resolvedGroup?.id,
      role: resolvedGroup?.role || formData.role || 'ADMIN',
      nome: formData.nome.trim(),
      email: formData.email.trim().toLowerCase(),
      telefone: formData.telefone ? unmask(formData.telefone) : undefined,
    };

    if (editingSolicitante) {
      updateMutation.mutate({ id: editingSolicitante.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleExecuteResetPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetTargetSolicitante) return;
    if (!resetPassword.trim()) {
      showAlert('Atenção', 'Informe ou gere uma senha temporária válida.', 'warning');
      return;
    }

    redefinirSenhaMutation.mutate({
      id: resetTargetSolicitante.id,
      data: {
        password: resetPassword.trim(),
        send_email: resetSendEmail,
        must_change_password: resetMustChange,
      },
    });
  };

  const isAllSelected =
    solicitantes.length > 0 && selectedIds.length === solicitantes.length;
  const isSomeSelected =
    selectedIds.length > 0 && selectedIds.length < solicitantes.length;

  return (
    <PageContainer className="pb-16">
      <PageHeader
        title="Solicitantes"
        description="Gerencie quem pode solicitar exames, agendar consultas e acessar os dados desta empresa"
        actions={
          isAdmin ? (
            <button
              onClick={handleOpenCreate}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 w-fit text-sm"
            >
              <PlusIcon className="w-5 h-5" />
              Novo Solicitante
            </button>
          ) : undefined
        }
      />

        {!isAdmin && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl text-xs sm:text-sm flex items-center gap-2.5">
            <ShieldCheckIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <span>
              <strong>Modo de Consulta:</strong> O gerenciamento e convite de novos solicitantes é restrito ao perfil de Administrador desta empresa.
            </span>
          </div>
        )}

        {/* Barra de Filtros Padronizada e Moderna */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Campo de Busca com debounce */}
            <div className="relative">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome, e-mail ou telefone..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-8 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Filtro de Status */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value as 'all' | 'active' | 'inactive');
                  setPage(1);
                }}
                className="w-full py-2 px-3 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
              >
                <option value="all">Status: Todos</option>
                <option value="active">Somente Ativos</option>
                <option value="inactive">Somente Inativos</option>
              </select>
            </div>

            {/* Filtro de Acesso ao Portal */}
            <div>
              <select
                value={filterHasUser}
                onChange={(e) => {
                  setFilterHasUser(e.target.value as 'all' | 'with_user' | 'without_user');
                  setPage(1);
                }}
                className="w-full py-2 px-3 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
              >
                <option value="all">Acesso: Todos</option>
                <option value="with_user">Com Usuário Ativo</option>
                <option value="without_user">Sem Usuário Vinculado</option>
              </select>
            </div>

            {/* Filtro de Perfil de Acesso */}
            <div>
              <select
                value={filterGroupId}
                onChange={(e) => {
                  const val = e.target.value;
                  setFilterGroupId(val === 'all' ? 'all' : Number(val));
                  setPage(1);
                }}
                className="w-full py-2 px-3 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
              >
                <option value="all">Perfil: Todos</option>
                {availableProfiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Filtros ativos na listagem de solicitantes
              </span>
              <button
                type="button"
                onClick={clearAllFilters}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
                Limpar Filtros
              </button>
            </div>
          )}
        </div>

        {/* Barra de Ações em Massa (quando há itens selecionados) */}
        {isAdmin && selectedIds.length > 0 && (
          <div className="sticky top-4 z-20 bg-slate-900 text-white px-4 py-3 rounded-xl shadow-xl flex flex-wrap items-center justify-between gap-3 border border-slate-700 animate-fadeIn">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center bg-emerald-500 text-slate-900 text-xs font-bold w-6 h-6 rounded-full">
                {selectedIds.length}
              </span>
              <span className="text-sm font-medium text-slate-200">
                {selectedIds.length === 1
                  ? '1 solicitante selecionado'
                  : `${selectedIds.length} solicitantes selecionados`}
              </span>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Ativar em lote */}
              <button
                type="button"
                onClick={handleBatchActivate}
                disabled={batchActionMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
                title="Ativar acesso dos solicitantes selecionados"
              >
                <CheckCircleIcon className="w-4 h-4" />
                Ativar em Lote
              </button>

              {/* Inativar em lote */}
              <button
                type="button"
                onClick={handleBatchDeactivate}
                disabled={batchActionMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-rose-600 hover:bg-rose-500 text-white transition-colors"
                title="Inativar acesso dos solicitantes selecionados"
              >
                <XCircleIcon className="w-4 h-4" />
                Inativar em Lote
              </button>

              {/* Reenviar Convites em lote */}
              <button
                type="button"
                onClick={handleBatchResendInvites}
                disabled={batchActionMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-600 transition-colors"
                title="Reenviar e-mail de acesso e senha provisória"
              >
                <PaperAirplaneIcon className="w-3.5 h-3.5" />
                Reenviar Convites
              </button>

              {/* Trocar Perfil em lote */}
              <button
                type="button"
                onClick={handleOpenBatchProfileModal}
                disabled={batchActionMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-600 transition-colors"
                title="Mudar o perfil de acesso dos selecionados"
              >
                <UserGroupIcon className="w-3.5 h-3.5" />
                Alterar Perfil
              </button>

              {/* Desmarcar */}
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="text-xs text-slate-400 hover:text-white px-2 py-1 transition-colors"
              >
                Desmarcar
              </button>
            </div>
          </div>
        )}

        {/* Tabela de Solicitantes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {isAdmin && (
                    <th className="py-3.5 px-4 w-12 text-center">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = isSomeSelected;
                        }}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded cursor-pointer"
                        title="Selecionar todos os registros visíveis"
                      />
                    </th>
                  )}
                  <th className="py-3.5 px-4">Nome / Solicitante</th>
                  <th className="py-3.5 px-4">Telefone</th>
                  <th className="py-3.5 px-4">Acesso ao Portal</th>
                  <th className="py-3.5 px-4">Perfil de Acesso</th>
                  <th className="py-3.5 px-4">Status</th>
                  {isAdmin && <th className="py-3.5 px-4 text-right">Ações</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={isAdmin ? 7 : 6}
                      className="py-12 text-center text-gray-400"
                    >
                      <div className="inline-flex items-center gap-2">
                        <svg
                          className="animate-spin h-5 w-5 text-emerald-600"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span>Carregando solicitantes...</span>
                      </div>
                    </td>
                  </tr>
                ) : solicitantes.length === 0 ? (
                  <tr>
                    <td
                      colSpan={isAdmin ? 7 : 6}
                      className="py-12 text-center text-gray-500"
                    >
                      <MagnifyingGlassIcon className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                      <p className="font-medium text-gray-700">
                        Nenhum solicitante encontrado
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {hasActiveFilters
                          ? 'Tente ajustar os filtros de busca para encontrar o que procura.'
                          : 'Clique em "Novo Solicitante" para cadastrar o primeiro acesso.'}
                      </p>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={clearAllFilters}
                          className="mt-3 inline-flex items-center text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg hover:bg-emerald-100 transition-colors"
                        >
                          Limpar todos os filtros
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  solicitantes.map((item) => {
                    const isSelected = selectedIds.includes(item.id);
                    const isAtivo = item.enabled !== false;
                    const matchedProfile = item.group_id
                      ? availableProfiles.find((p) => p.id === item.group_id)
                      : availableProfiles.find(
                          (p) =>
                            p.name === item.group_name || p.role === item.role,
                        );

                    const profileBg =
                      matchedProfile?.bg ||
                      'bg-indigo-50 border-indigo-200 text-indigo-700';
                    const profileLabel =
                      matchedProfile?.label ||
                      item.group_name ||
                      item.role ||
                      'Administrador';

                    return (
                      <tr
                        key={item.id}
                        className={`transition-colors ${
                          isSelected
                            ? 'bg-emerald-50/60'
                            : 'hover:bg-gray-50/70'
                        } ${!isAtivo ? 'opacity-70 bg-gray-50/30' : ''}`}
                      >
                        {isAdmin && (
                          <td className="py-3.5 px-4 text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSelectRow(item.id)}
                              className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded cursor-pointer"
                            />
                          </td>
                        )}

                        {/* Nome / Avatar / E-mail */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-xs flex-shrink-0">
                              {(item.nome || item.email || '?')
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-gray-900 truncate">
                                  {item.nome || 'Sem nome informado'}
                                </span>
                                {(item.is_grupo_economico || item.grupo_economico_id) && (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800 border border-purple-200"
                                    title={`Solicitante vinculado ao Grupo Econômico (${item.grupo_economico_nome || grupoEconomicoMeta?.nome || 'Matriz & Filiais'})`}
                                  >
                                    <BuildingOffice2Icon className="w-3 h-3 text-purple-600" />
                                    Grupo Econômico
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {item.email}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Telefone */}
                        <td className="py-3.5 px-4 text-gray-600 whitespace-nowrap text-xs">
                          {formatPhone(item.telefone) || '-'}
                        </td>

                        {/* Acesso ao Portal (Toggle Switch Travado quando sem usuário) */}
                        <td className="py-3.5 px-4 whitespace-nowrap">
                          {(() => {
                            const hasUser = Boolean(item.user || item.user_id);
                            const isPortalActive = Boolean(
                              hasUser && item.user?.enabled !== false && isAtivo,
                            );
                            const isToggleDisabled =
                              !hasUser ||
                              !isAdmin ||
                              !isAtivo ||
                              togglePortalAccessMutation.isPending;

                            return (
                              <div
                                className="flex items-center gap-2"
                                title={
                                  !hasUser
                                    ? 'Este solicitante não possui usuário vinculado. Use o botão de convite para criar o acesso ao portal.'
                                    : !isAtivo
                                      ? 'Ative o solicitante primeiro para liberar o acesso ao portal.'
                                      : isPortalActive
                                        ? 'Acesso ao Portal Ativo'
                                        : 'Acesso ao Portal Inativo'
                                }
                              >
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={isPortalActive}
                                  disabled={isToggleDisabled}
                                  onClick={() => {
                                    if (isToggleDisabled) return;
                                    togglePortalAccessMutation.mutate({
                                      id: item.id,
                                      enabled: !isPortalActive,
                                    });
                                  }}
                                  className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                                    isPortalActive ? 'bg-emerald-600' : 'bg-gray-300'
                                  } ${
                                    isToggleDisabled
                                      ? 'opacity-50 cursor-not-allowed'
                                      : 'cursor-pointer'
                                  }`}
                                >
                                  <span
                                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                      isPortalActive ? 'translate-x-4' : 'translate-x-0'
                                    }`}
                                  />
                                </button>
                                <span
                                  className={`text-xs font-medium ${
                                    !hasUser
                                      ? 'text-gray-400'
                                      : isPortalActive
                                        ? 'text-emerald-700 font-semibold'
                                        : 'text-gray-500'
                                  }`}
                                >
                                  {!hasUser
                                    ? 'Sem usuário'
                                    : isPortalActive
                                      ? 'Ativo'
                                      : 'Inativo'}
                                </span>
                              </div>
                            );
                          })()}
                        </td>

                        {/* Perfil de Acesso */}
                        <td className="py-3.5 px-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${profileBg}`}
                          >
                            {profileLabel}
                          </span>
                        </td>

                        {/* Status (Toggle Switch Interativo com Confirmação) */}
                        <td className="py-3.5 px-4 whitespace-nowrap">
                          {isAdmin ? (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={isAtivo}
                                onClick={() => handleToggleStatus(item)}
                                disabled={toggleStatusMutation.isPending}
                                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                                  isAtivo ? 'bg-emerald-600' : 'bg-gray-300'
                                }`}
                                title={
                                  isAtivo
                                    ? 'Clique para inativar acesso'
                                    : 'Clique para ativar acesso'
                                }
                              >
                                <span
                                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                    isAtivo ? 'translate-x-4' : 'translate-x-0'
                                  }`}
                                />
                              </button>
                              <span
                                className={`text-xs font-medium ${
                                  isAtivo ? 'text-emerald-700' : 'text-gray-400'
                                }`}
                              >
                                {isAtivo ? 'Ativo' : 'Inativo'}
                              </span>
                            </div>
                          ) : (
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                                isAtivo
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  isAtivo ? 'bg-emerald-500' : 'bg-gray-400'
                                }`}
                              />
                              {isAtivo ? 'Ativo' : 'Inativo'}
                            </span>
                          )}
                        </td>

                        {/* Ações Diretas */}
                        {isAdmin && (
                          <td className="py-3.5 px-4 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              {/* Reenviar Convite */}
                              <button
                                type="button"
                                onClick={() => handleReenviarConvite(item)}
                                title="Reenviar convite de acesso por e-mail"
                                className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              >
                                <PaperAirplaneIcon className="w-4 h-4" />
                              </button>

                              {/* Redefinir Senha */}
                              <button
                                type="button"
                                onClick={() => handleOpenResetPassword(item)}
                                title="Redefinir senha provisória"
                                className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              >
                                <KeyIcon className="w-4 h-4" />
                              </button>

                              {/* Editar */}
                              <button
                                type="button"
                                onClick={() => handleOpenEdit(item)}
                                title="Editar dados do solicitante"
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>

                              {/* Remover / Inativar */}
                              <button
                                type="button"
                                onClick={() => handleRemove(item)}
                                title="Desativar solicitante"
                                className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
            <div>
              {pagination.total === 0
                ? 'Nenhum registro'
                : `Mostrando ${(pagination.page - 1) * pagination.limit + 1} a ${Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  )} de ${pagination.total} registro(s)`}
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <span>Itens por página:</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="py-1 px-2 border border-gray-300 rounded bg-white text-xs"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                  className="p-1.5 rounded border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Página Anterior"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <span className="px-2 font-medium">
                  {pagination.page} de {pagination.totalPages || 1}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={pagination.page >= (pagination.totalPages || 1)}
                  className="p-1.5 rounded border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Próxima Página"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

      {/* Modal de Criação / Edição */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editingSolicitante ? 'Editar Solicitante' : 'Novo Solicitante'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome Completo <span className="text-red-500 font-bold">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
              placeholder="Nome completo do solicitante"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              E-mail Profissional <span className="text-red-500 font-bold">*</span>
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
              placeholder="usuario@empresa.com.br"
            />
          </div>

          <FormField label="Telefone / WhatsApp">
            <FormattedInput
              type="phone"
              value={formData.telefone}
              onChange={(val) => setFormData({ ...formData, telefone: val })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
              placeholder="(00) 00000-0000"
            />
          </FormField>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Perfil de Acesso <span className="text-red-500 font-bold">*</span>
            </label>
            <select
              value={activeGroupId}
              onChange={(e) => {
                const selId = Number(e.target.value);
                const selProfile =
                  availableProfiles.find((p) => p.id === selId) ||
                  availableProfiles[0];
                setFormData({
                  ...formData,
                  group_id: selProfile.id,
                  role: selProfile.role,
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm bg-white"
            >
              {availableProfiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label} {p.desc ? `- ${p.desc}` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Define quais módulos e ações este usuário poderá acessar no portal desta empresa.
            </p>
          </div>

          {Boolean(grupoEconomicoMeta?.id) && (
            <div className="space-y-2 pt-3 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700">
                Vínculo do Solicitante
              </label>
              <div className="space-y-2">
                <label className="flex items-start gap-2.5 text-xs font-medium text-gray-800 cursor-pointer p-3 rounded-lg bg-purple-50/50 border border-purple-200">
                  <input
                    type="radio"
                    name="scope_type"
                    checked={formData.scope_type === 'grupo_economico' || formData.scope_type === 'todas_grupo' || Boolean(formData.vincular_grupo_economico)}
                    onChange={() =>
                      setFormData({
                        ...formData,
                        scope_type: 'grupo_economico',
                        vincular_grupo_economico: true,
                      })
                    }
                    className="mt-0.5 text-purple-600 focus:ring-purple-500"
                  />
                  <div>
                    <span className="font-bold text-purple-950 block">
                      Grupo Econômico ({grupoEconomicoMeta?.nome || editingSolicitante?.grupo_economico_nome || 'Matriz & Filiais'})
                    </span>
                    <span className="text-[11px] text-purple-800/80">
                      Disponível para todas as empresas do grupo econômico.
                    </span>
                  </div>
                </label>

                <label className="flex items-start gap-2.5 text-xs font-medium text-gray-800 cursor-pointer p-3 rounded-lg hover:bg-gray-50 border border-gray-200">
                  <input
                    type="radio"
                    name="scope_type"
                    checked={formData.scope_type === 'empresa_atual' && !formData.vincular_grupo_economico}
                    onChange={() =>
                      setFormData({
                        ...formData,
                        scope_type: 'empresa_atual',
                        vincular_grupo_economico: false,
                      })
                    }
                    className="mt-0.5 text-purple-600 focus:ring-purple-500"
                  />
                  <div>
                    <span className="font-bold text-gray-900 block">
                      Apenas esta empresa ({empresa?.nome || 'Empresa Atual'})
                    </span>
                    <span className="text-[11px] text-gray-500">
                      Restrito exclusivamente a esta empresa.
                    </span>
                  </div>
                </label>
              </div>
            </div>
          )}

          {!editingSolicitante && (
            <div className="space-y-4 pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.create_portal_user}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        create_portal_user: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded cursor-pointer"
                  />
                  Criar Usuário de Acesso ao Portal do Cliente
                </label>
              </div>

              {formData.create_portal_user && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3.5">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-gray-700">
                        Senha Provisória
                      </label>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            initial_password: generateRandomPassword(),
                          })
                        }
                        className="text-xs text-emerald-600 hover:text-emerald-800 font-medium inline-flex items-center gap-1"
                      >
                        <ArrowPathIcon className="w-3.5 h-3.5" />
                        Gerar Nova
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type={showInitialPassword ? 'text' : 'password'}
                        value={formData.initial_password}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            initial_password: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg font-mono text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Senha provisória"
                      />
                      <button
                        type="button"
                        onClick={() => setShowInitialPassword(!showInitialPassword)}
                        className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        {showInitialPassword ? (
                          <EyeSlashIcon className="w-4 h-4" />
                        ) : (
                          <EyeIcon className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 pt-1">
                    <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.must_change_password}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            must_change_password: e.target.checked,
                          })
                        }
                        className="h-3.5 w-3.5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded cursor-pointer"
                      />
                      Exigir troca de senha no primeiro acesso
                    </label>

                    <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.send_access_email}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            send_access_email: e.target.checked,
                          })
                        }
                        className="h-3.5 w-3.5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded cursor-pointer"
                      />
                      Enviar e-mail com credenciais e link de acesso imediatamente
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {editingSolicitante?.user && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
              Este solicitante está vinculado ao usuário{' '}
              <strong>{editingSolicitante.user.email}</strong>. Para alterar
              credenciais ou senha, utilize o botão de chave na tabela de ações.
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium text-sm shadow-sm"
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Salvando...'
                : editingSolicitante
                ? 'Salvar Alterações'
                : 'Cadastrar Solicitante'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal de Redefinir Senha */}
      <Modal
        isOpen={isResetPasswordModalOpen && !!resetTargetSolicitante}
        onClose={() => setIsResetPasswordModalOpen(false)}
        title="Redefinir Senha de Acesso"
        size="md"
      >
        {resetTargetSolicitante && (
          <form onSubmit={handleExecuteResetPassword} className="space-y-4">
            <p className="text-xs text-gray-500">
              Redefinindo senha para{' '}
              <strong className="text-gray-700">
                {resetTargetSolicitante.nome || resetTargetSolicitante.email}
              </strong>{' '}
              ({resetTargetSolicitante.email})
            </p>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-gray-700">
                  Nova Senha Provisória <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setResetPassword(generateRandomPassword())}
                  className="text-xs text-emerald-600 hover:text-emerald-800 font-medium inline-flex items-center gap-1"
                >
                  <ArrowPathIcon className="w-3.5 h-3.5" />
                  Gerar Outra
                </button>
              </div>
              <div className="relative">
                <input
                  type={showResetPassword ? 'text' : 'password'}
                  required
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setShowResetPassword(!showResetPassword)}
                  className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  {showResetPassword ? (
                    <EyeSlashIcon className="w-4 h-4" />
                  ) : (
                    <EyeIcon className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resetMustChange}
                  onChange={(e) => setResetMustChange(e.target.checked)}
                  className="h-3.5 w-3.5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded cursor-pointer"
                />
                Forçar troca de senha no próximo login
              </label>

              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resetSendEmail}
                  onChange={(e) => setResetSendEmail(e.target.checked)}
                  className="h-3.5 w-3.5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded cursor-pointer"
                />
                Enviar a nova senha para o e-mail do usuário
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setIsResetPasswordModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={redefinirSenhaMutation.isPending}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 shadow-sm"
              >
                {redefinirSenhaMutation.isPending
                  ? 'Salvando...'
                  : 'Salvar e Copiar Senha'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Modal de Alteração de Perfil em Massa */}
      <Modal
        isOpen={isBatchProfileModalOpen}
        onClose={() => setIsBatchProfileModalOpen(false)}
        title="Alterar Perfil de Acesso em Lote"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            Selecione o novo perfil que será aplicado aos{' '}
            <strong className="text-gray-900">{selectedIds.length}</strong>{' '}
            solicitante(s) selecionado(s):
          </p>

          <div className="space-y-2">
            {availableProfiles.map((p) => {
              const isSelected = batchTargetProfileId === p.id;
              return (
                <div
                  key={p.id}
                  onClick={() => setBatchTargetProfileId(p.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    isSelected
                      ? 'border-emerald-500 bg-emerald-50/50 ring-2 ring-emerald-500'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">
                      {p.label}
                    </span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border ${p.bg}`}
                    >
                      {p.role}
                    </span>
                  </div>
                  {p.desc && (
                    <p className="text-xs text-gray-500 mt-1">{p.desc}</p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setIsBatchProfileModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleExecuteBatchProfile}
              disabled={batchActionMutation.isPending}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 shadow-sm"
            >
              {batchActionMutation.isPending
                ? 'Atualizando...'
                : 'Confirmar Alteração'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Diálogos Globais */}
      <AlertDialog
        isOpen={alertState.isOpen}
        onClose={() => setAlertState((prev) => ({ ...prev, isOpen: false }))}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
      />

      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={() => setConfirmState((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
      />
    </PageContainer>
  );
}
